import merge from "deepmerge";
import { useAtomValue } from "jotai";
import type { NextSeoProps } from "next-seo";
import { useEffect } from "react";
import type { FunctionComponent } from "react";
import { graphql, useFragment } from "react-relay";

import type { Props as PageSeoProps } from "~/components/meta";
import { defaultAdditionalMetaTags, PageSeo } from "~/components/meta";
import { DefaultTitleTemplate } from "~/components/meta/consts";
import { useDeepLinkTags, useDynamicMetaTags, useKeywordMetaTags } from "~/components/meta/hooks";
import { trackingAtom } from "~/components/tracking";
import { useCanonicalUrl } from "~/lib/hooks";
import { getIsSubSection } from "~/lib/router/helpers";
import type { sectionSeoSection$key } from "~/queries/__generated__/sectionSeoSection.graphql";

import { useSeoImage } from "./hook";

type Props = PageSeoProps & {
  reference: sectionSeoSection$key;
};

export const SectionSeo: FunctionComponent<Props> = ({ reference: reference_, ...extraProps }) => {
  const section = useFragment(
    graphql`
      fragment sectionSeoSection on Section {
        name
        description {
          text
        }
        entityId
        entityUuid
        image(filter: { type: OG }) {
          url
        }
        parentSection
        urlAlias
        ...hooksUseDeepLinkTagsBase
        ...hooksUseDynamicMetaTags
      }
    `,
    reference_,
  );

  const metaTags = useDynamicMetaTags(section);
  const keywordTags = useKeywordMetaTags(metaTags.controlled.keywords ?? section.name);
  const deepLinkTags = useDeepLinkTags(section);

  const titleTemplate = metaTags.controlled.title ? "%s" : DefaultTitleTemplate;
  const title = metaTags.controlled.title ?? section.name;

  const description = metaTags.controlled.description ?? section.description?.text ?? undefined;

  const { canonicalUrl } = useCanonicalUrl(section.urlAlias);

  const ogImageUuid = getIsSubSection({
    entityId: section.entityId,
    parentSection: section.parentSection,
  })
    ? section.parentSection
    : section.entityUuid;

  const sectionOgImage = useSeoImage(ogImageUuid);

  const openGraph: NextSeoProps["openGraph"] = { description, title, url: canonicalUrl };
  if (section.image?.url) {
    openGraph.images = [{ url: section.image.url }];
  } else if (sectionOgImage) {
    openGraph.images = [{ url: sectionOgImage }];
  }

  const defaultSectionProps: NextSeoProps = {
    additionalMetaTags: [...keywordTags, ...deepLinkTags, ...defaultAdditionalMetaTags],
    canonical: canonicalUrl,
    description,
    openGraph,
    title,
    titleTemplate,
  };

  const mergedProps = merge(defaultSectionProps, extraProps);

  const { trackPageView } = useAtomValue(trackingAtom);

  // TODO: `slideIndex` & `name` needed later when implementing other section pages with slide
  useEffect(() => {
    const { entityId, entityUuid, name, urlAlias } = section;
    trackPageView?.({
      contentType: "Section",
      entityId,
      entityUuid,
      page: "Section",
      pageTitle: title,
      path: urlAlias,
      primarySection: name,
      sections: name,
    });
  }, [trackPageView, section, title]);

  return <PageSeo {...mergedProps} />;
};

SectionSeo.displayName = "SectionSeo";
